import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { CheckCircle2, Clock, AlertTriangle, PauseCircle, BarChart3, Users } from "lucide-react";

function Kpi({ label, value, hint, tone = "royal", testid }) {
  const tones = {
    royal: "border-royal-700/20 bg-white",
    red: "border-[#B02A00]/30 bg-[#FFF5F0]",
    gold: "border-gold/50 bg-gold/10",
    green: "border-[#175E33]/20 bg-[#E9F5EE]",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.royal}`} data-testid={testid}>
      <div className="label-caps">{label}</div>
      <div className="font-display text-3xl text-royal-800 mt-1">{value}</div>
      {hint && <div className="text-xs text-ink/50 mt-1 font-mono-tight">{hint}</div>}
    </div>
  );
}

export default function ManagementAnalytics() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [focus, setFocus] = useState("");

  useEffect(() => { api.get("/management-analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  const focused = focus ? d.per_manager.find((m) => m.manager_id === focus) : null;

  return (
    <div>
      <PageHeader title="Management Analytics" description={`Operational KPIs across your workspace — ${user.role === "SuperAdmin" ? "SuperAdmin" : "Operation Head"} view only.`} />
      <div className="p-8 space-y-6">
        <div>
          <div className="label-caps mb-2">Work Order Metrics</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="wo-kpis">
            <Kpi testid="wo-processed-today" label="Processed today" value={d.wo.processed_today} />
            <Kpi testid="wo-submitted-today" label="Submitted today" value={d.wo.submitted_today} />
            <Kpi testid="wo-completed" label="Completed" value={d.wo.completed} tone="green" />
            <Kpi testid="wo-pending" label="Pending" value={d.wo.pending} />
            <Kpi testid="wo-awaiting-review" label="Awaiting review" value={d.wo.awaiting_review} tone="gold" />
            <Kpi testid="wo-in-progress" label="In progress" value={d.wo.in_progress} />
          </div>
        </div>

        <div>
          <div className="label-caps mb-2">Project Metrics</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="project-kpis">
            <Kpi testid="proj-total" label="Total" value={d.project.total} />
            <Kpi testid="proj-status-changes" label="Status changes today" value={d.project.status_changes_today} />
            <Kpi testid="proj-on-track" label="On track" value={d.project.on_track} tone="green" />
            <Kpi testid="proj-nearing" label="Nearing completion" value={d.project.nearing_completion} tone="gold" />
            <Kpi testid="proj-on-hold" label="On hold" value={d.project.on_hold} />
            <Kpi testid="proj-inactive" label="Inactive 14d+" value={d.project.inactive} />
            <Kpi testid="proj-delayed" label="Delayed / overdue" value={d.project.delayed} tone="red" />
            <Kpi testid="proj-completed" label="Completed" value={d.project.completed} tone="green" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Kpi testid="closure-rate" label="Project closure rate" value={`${d.closure_rate}%`} hint="Completed ÷ total projects" tone="green" />
          <Kpi testid="efficiency" label="Operational efficiency" value={`${d.efficiency_pct}%`} hint="Completed WOs ÷ total WOs" tone="royal" />
        </div>

        <div className="bg-white border border-black/10 rounded-2xl overflow-hidden" data-testid="per-manager-table">
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
            <div className="label-caps flex items-center gap-2 text-royal-800"><Users size={12} /> Per-Manager Breakdown</div>
            <select data-testid="manager-focus" value={focus} onChange={(e) => setFocus(e.target.value)} className="h-9 border border-black/15 rounded-lg px-2 text-sm bg-white">
              <option value="">All managers</option>
              {d.per_manager.map((m) => <option key={m.manager_id} value={m.manager_id}>{m.manager_name}</option>)}
            </select>
          </div>
          <table className="fms-table w-full">
            <thead><tr><th>Manager</th><th>Projects</th><th>On track</th><th>Delayed</th><th>WOs</th><th>Submitted</th><th>In Progress</th><th>Completed</th></tr></thead>
            <tbody>
              {(focused ? [focused] : d.per_manager).map((m) => (
                <tr key={m.manager_id} data-testid={`mgr-row-${m.manager_id}`}>
                  <td>{m.manager_name}</td>
                  <td className="font-mono-tight">{m.projects}</td>
                  <td className="font-mono-tight text-[#175E33]">{m.projects_on_track}</td>
                  <td className="font-mono-tight text-[#B02A00]">{m.projects_delayed}</td>
                  <td className="font-mono-tight">{m.wos_total}</td>
                  <td className="font-mono-tight">{m.wos_submitted}</td>
                  <td className="font-mono-tight">{m.wos_in_progress}</td>
                  <td className="font-mono-tight">{m.wos_completed}</td>
                </tr>
              ))}
              {d.per_manager.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-neutral-500">No managers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
